import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { 
  Play, Plus, Search, Eye, CheckCircle, XCircle, 
  Clock, AlertCircle, FileText, RefreshCw
} from 'lucide-react';
import { payrollAPI } from '../../../services/api';
import { toast } from 'sonner';

const PayrollRuns = () => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [payrollAreas, setPayrollAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [executing, setExecuting] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [runsRes, areasRes] = await Promise.all([
        payrollAPI.getRuns(),
        payrollAPI.getPayrollAreas()
      ]);
      setRuns(runsRes.data || []);
      setPayrollAreas(areasRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load payroll runs');
    } finally {
      setLoading(false);
    }
  };

  const filteredRuns = runs.filter(run => {
    const matchesSearch = 
      (run.run_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (run.payroll_area_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || run.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleExecute = async (runId) => {
    setExecuting(runId);
    try {
      await payrollAPI.executeRun(runId, 'ADMIN');
      toast.success('Payroll executed successfully');
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to execute payroll');
      }
    } finally {
      setExecuting(null);
    }
  };

  const handleApprove = async (runId) => {
    try {
      await payrollAPI.approveRun(runId, 'ADMIN');
      toast.success('Payroll approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve payroll');
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      'DRAFT': { color: 'bg-gray-100 text-gray-700', icon: Clock, label: 'Draft' },
      'RELEASED': { color: 'bg-orange-100 text-orange-700', icon: Play, label: 'Released' },
      'PROCESSING': { color: 'bg-yellow-100 text-yellow-700', icon: RefreshCw, label: 'Processing' },
      'CHECK_RESULTS': { color: 'bg-orange-100 text-orange-700', icon: AlertCircle, label: 'Check Results' },
      'CORRECTION': { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Correction' },
      'COMPLETED': { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Completed' },
      'POSTED': { color: 'bg-orange-100 text-orange-700', icon: FileText, label: 'Posted' }
    };
    return configs[status] || configs['DRAFT'];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="payroll-runs-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/payroll')}>Payroll</span>
            <span>/</span>
            <span>Payroll Runs</span>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payroll Runs</h1>
              <p className="text-gray-600 mt-1">Create, execute, and manage payroll runs</p>
            </div>
            <Button onClick={() => setShowForm(true)} data-testid="btn-create-run">
              <Plus className="h-4 w-4 mr-2" />
              Create Payroll Run
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {['DRAFT', 'CHECK_RESULTS', 'COMPLETED', 'POSTED'].map((status) => {
            const config = getStatusConfig(status);
            const count = runs.filter(r => r.status === status).length;
            return (
              <Card key={status} className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.color}`}>
                    <config.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{config.label}</p>
                    <p className="text-xl font-semibold">{count}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search by run number or area..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="RELEASED">Released</option>
            <option value="CHECK_RESULTS">Check Results</option>
            <option value="COMPLETED">Completed</option>
            <option value="POSTED">Posted</option>
          </select>
        </div>

        {/* Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : filteredRuns.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No payroll runs found. Click "Create Payroll Run" to start.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Run Number</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Payroll Area</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Period</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Employees</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Total Net</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredRuns.map((run) => {
                    const statusConfig = getStatusConfig(run.status);
                    return (
                      <tr key={run.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-orange-600">{run.run_number}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{run.payroll_area_name || run.payroll_area_id}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{run.period_year}/{run.period_number}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            run.run_type === 'REGULAR' ? 'bg-orange-100 text-orange-700' :
                            run.run_type === 'BONUS' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {run.run_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{run.employee_count || 0}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-700">{formatCurrency(run.total_net)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusConfig.color}`}>
                            <statusConfig.icon className="h-3 w-3" />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => navigate(`/hcm/payroll/runs/${run.id}`)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {(run.status === 'DRAFT' || run.status === 'RELEASED' || run.status === 'CORRECTION') && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-orange-600"
                                onClick={() => handleExecute(run.id)}
                                disabled={executing === run.id}
                              >
                                {executing === run.id ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Play className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                            {run.status === 'CHECK_RESULTS' && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-green-600"
                                onClick={() => handleApprove(run.id)}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Create Form Modal */}
        {showForm && (
          <PayrollRunForm 
            payrollAreas={payrollAreas}
            onClose={() => setShowForm(false)}
            onSave={() => { setShowForm(false); loadData(); }}
          />
        )}
      </div>
    </Layout>
  );
};

// Payroll Run Form Modal
const PayrollRunForm = ({ payrollAreas, onClose, onSave }) => {
  const currentDate = new Date();
  const [formData, setFormData] = useState({
    payroll_area_id: '',
    period_year: currentDate.getFullYear(),
    period_number: currentDate.getMonth() + 1,
    run_type: 'REGULAR',
    run_description: '',
    include_retroactive: true
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.payroll_area_id) {
      toast.error('Please select a payroll area');
      return;
    }

    setLoading(true);
    try {
      await payrollAPI.createRun(formData);
      toast.success('Payroll run created successfully');
      onSave();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to create payroll run');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">Create Payroll Run</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payroll Area *</label>
            <select
              value={formData.payroll_area_id}
              onChange={(e) => setFormData({...formData, payroll_area_id: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            >
              <option value="">Select Payroll Area</option>
              {payrollAreas.filter(a => a.status === 'Active').map(area => (
                <option key={area.payroll_area_id} value={area.payroll_area_id}>
                  {area.name} ({area.payroll_area_id})
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
              <Input 
                type="number"
                value={formData.period_year}
                onChange={(e) => setFormData({...formData, period_year: parseInt(e.target.value)})}
                min={2020}
                max={2100}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Period *</label>
              <Input 
                type="number"
                value={formData.period_number}
                onChange={(e) => setFormData({...formData, period_number: parseInt(e.target.value)})}
                min={1}
                max={12}
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Run Type</label>
            <select
              value={formData.run_type}
              onChange={(e) => setFormData({...formData, run_type: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="REGULAR">Regular Payroll</option>
              <option value="BONUS">Bonus Payment</option>
              <option value="CORRECTION">Correction Run</option>
              <option value="SIMULATION">Simulation</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <Input 
              value={formData.run_description}
              onChange={(e) => setFormData({...formData, run_description: e.target.value})}
              placeholder="Optional description"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="include_retroactive"
              checked={formData.include_retroactive}
              onChange={(e) => setFormData({...formData, include_retroactive: e.target.checked})}
              className="rounded border-gray-300"
            />
            <label htmlFor="include_retroactive" className="text-sm text-gray-700">
              Include retroactive calculations
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Creating...' : 'Create Run'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayrollRuns;
